import React, {useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

/**
 * Dialog for selecting a passage to cite evidence to
 * Shows full list of report fragments with non-truncated text
 */
export default function SelectPassageDialog({
                                                open,
                                                fragmentMarks,
                                                selectedFragmentUid,
                                                onCiteToFragment,
                                                onClose,
                                            }) {
    const [selectedPassageUid, setSelectedPassageUid] = useState(selectedFragmentUid);

    const handleCite = () => {
        if (selectedPassageUid) {
            onCiteToFragment(selectedPassageUid);
            onClose();
        }
    };

    const handleClose = () => {
        setSelectedPassageUid(selectedFragmentUid);
        onClose();
    };

    // Sort fragments to show selected first, then others in order
    const sortedFragments = React.useMemo(() => {
        const fragments = [...(fragmentMarks || [])];
        if (selectedFragmentUid) {
            const selectedIndex = fragments.findIndex(f => f.uid === selectedFragmentUid);
            if (selectedIndex > -1) {
                const [selected] = fragments.splice(selectedIndex, 1);
                fragments.unshift(selected);
            }
        }
        return fragments;
    }, [fragmentMarks, selectedFragmentUid]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {sx: {minHeight: 450, height: '558px'}}
            }}
        >
            <DialogTitle sx={{p: 2, pb: 1.5, pt: 1.5}}>
                Select Passage to Cite
            </DialogTitle>

            <DialogContent sx={{p: 0, minHeight: 250}}>
                {sortedFragments.length === 0 ? (
                    <Box sx={{px: 3, py: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic'}}>
                        No report fragments found
                    </Box>
                ) : (
                    <List sx={{width: '100%', py: 0}}>
                        {sortedFragments.map(({uid, text}) => (
                            <ListItem key={uid} disablePadding>
                                <ListItemButton
                                    selected={selectedPassageUid === uid}
                                    onClick={() => setSelectedPassageUid(uid)}
                                    sx={{py: 1.5}}
                                >
                                    <ListItemIcon sx={{minWidth: 40}}>
                                        <FormatQuoteIcon
                                            color={uid === selectedFragmentUid ? "primary" : "inherit"}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={text || '(no text)'}
                                        slotProps={{
                                            primary: {
                                                sx: {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleCite}
                    variant="contained"
                    disabled={!selectedPassageUid}
                >
                    Cite
                </Button>
            </DialogActions>
        </Dialog>
    );
}
